import api from './baseApi';
import { TribeMembershipDetails } from './circles';
import { SortOrder } from './common';
import { Post } from './posts';

export enum EngagementSortBy {
  DATE = 'date',
  VIEWS = 'views',
  COMMENTS = 'comments',
  TOTAL_COMPLETED_VIEWS = 'total_completed_views',
}

export enum EngagementWatchStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export enum EngagementCommentType {
  VIDEO = 'VIDEO',
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
}

export enum EngagementType {
  CHALLENGE_VIEWED = 'CHALLENGE_VIEWED',
  COMMENTS_VIEWED = 'COMMENTS_VIEWED', // in webapp, wide screen will directly show comments - will need to send this event but wait for 2s like challengeViewed; in mobile view will need to send after click the comments icon
  COMMENT_LIKE = 'COMMENT_LIKE',
  COMMENT_UNLIKE = 'COMMENT_UNLIKE',
  COMMENT_CREATE = 'COMMENT_CREATE',
  COMMENT_REPLY = 'COMMENT_REPLY',
  COMMENT_UPDATE = 'COMMENT_UPDATE',
  COMMENT_DELETE = 'COMMENT_DELETE',
  // CHALLENGE_LIKE = 'CHALLENGE_LIKE',  // Doesn't exist yet
  // CHALLENGE_UNLIKE = 'CHALLENGE_UNLIKE',
  MESSAGE_SENT = 'MESSAGE_SENT',
  MESSAGE_REPLIED = 'MESSAGE_REPLIED',
}

export interface Engagement {
  _id: string;
  createdAt: string;
  updatedAt: string;

  user_id: string;
  tribe_id: string;
  type: EngagementType;
  userAgent: string;

  post_id?: string;
  commentTypes?: EngagementCommentType[];
  duration?: number;
  videoLength?: number;
  completePlayedCount?: number;
}

export interface ChallengeEngagementMetrics {
  inProgress: number;
  completed: number;
  totalCompletedViews: number;
  comments: number;
  likes: number;
  forumViews: number;
  videoLength?: number;
  duration?: number;
  lastViewedAt?: number;
}

export interface ChallengeEngagementPostMetrics {
  post: Post;
  engagement: ChallengeEngagementMetrics;
}

export interface PostMemberEngagementMetrics {
  member: TribeMembershipDetails;
  engagement: ChallengeEngagementMetrics;
}

export interface NotEngagedMemberMetrics {
  member: TribeMembershipDetails;
  lastActivity?: string;
  daysSinceLastActivity?: number;
  totalEngagements: number;
}

export const engagementsApi = {
  upsertWatchedEngagements: async ({
    type,
    duration,
    postId,
    watchStatus,
  }: {
    type: EngagementType;
    postId: string;
    duration?: number;
    watchStatus?: EngagementWatchStatus;
  }): Promise<Engagement> => {
    const axiosResponse = await api.put(`/posts/${postId}/engagements`, {
      type,
      ...(duration && { duration }),
      ...(watchStatus !== undefined && { watchStatus }),
    });
    return axiosResponse.data.result;
  },

  getTribePostsEngagements: async ({
    tribeId,
    page,
    pageSize,
    from,
    to,
    sortBy = EngagementSortBy.DATE,
    sortOrder = SortOrder.DESC,
  }: {
    tribeId: string;
    page: number;
    pageSize?: number;
    from?: Date;
    to?: Date;
    sortBy?: EngagementSortBy;
    sortOrder?: SortOrder;
  }): Promise<ChallengeEngagementPostMetrics[]> => {
    const response = await api.get(`/engagements/tribes/${tribeId}/posts`, {
      params: {
        page,
        ...(pageSize && { pageSize }),
        ...(from && { from: from.toISOString() }),
        ...(to && { to: to.toISOString() }),
        sortBy,
        sortOrder,
      },
    });
    return response.data.result;
  },

  getPostViewers: async ({
    postId,
    page,
    pageSize,
  }: {
    postId: string;
    page: number;
    pageSize?: number;
  }): Promise<PostMemberEngagementMetrics[]> => {
    const response = await api.get(`/engagements/posts/${postId}/members`, {
      params: {
        page,
        ...(pageSize && { pageSize }),
      },
    });
    return response.data.result;
  },

  getNotEngagedMembers: async ({
    tribeId,
    page = 1,
    pageSize = 50,
    from,
    to,
    minEngagementThreshold = 0,
  }: {
    tribeId: string;
    page?: number;
    pageSize?: number;
    from?: Date;
    to?: Date;
    minEngagementThreshold?: number;
  }): Promise<NotEngagedMemberMetrics[]> => {
    const response = await api.get(
      `/engagements/tribes/${tribeId}/not-engaged-members`,
      {
        params: {
          page,
          pageSize,
          ...(from && { from: from.toISOString() }),
          ...(to && { to: to.toISOString() }),
          minEngagementThreshold,
        },
      }
    );
    return response.data.result;
  },

  getPostNotEngagedMembers: async ({
    postId,
    page = 1,
    pageSize = 50,
    from,
    to,
    minEngagementThreshold = 0,
  }: {
    postId: string;
    page?: number;
    pageSize?: number;
    from?: Date;
    to?: Date;
    minEngagementThreshold?: number;
  }): Promise<NotEngagedMemberMetrics[]> => {
    const response = await api.get(
      `/engagements/posts/${postId}/not-engaged-members`,
      {
        params: {
          page,
          pageSize,
          ...(from && { from: from.toISOString() }),
          ...(to && { to: to.toISOString() }),
          minEngagementThreshold,
        },
      }
    );
    return response.data.result;
  },

  getTribeMemberEngagements: async ({
    tribeId,
    page = 1,
    pageSize = 50,
    from,
    to,
  }: {
    tribeId: string;
    page?: number;
    pageSize?: number;
    from?: Date;
    to?: Date;
  }): Promise<PostMemberEngagementMetrics[]> => {
    const response = await api.get(`/engagements/tribes/${tribeId}/members`, {
      params: {
        page,
        pageSize,
        ...(from && { from: from.toISOString() }),
        ...(to && { to: to.toISOString() }),
      },
    });
    return response.data.result;
  },
};
